import React, { createContext, useContext, useState, useEffect, useRef, useCallback } from 'react';
 
import { 
  collection, 
  addDoc, 
  updateDoc, 
  deleteDoc, 
  doc, 
  onSnapshot,
  query, 
  orderBy, 
  serverTimestamp 
} from 'firebase/firestore';
import { 
  ref, 
  uploadBytes, 
  getDownloadURL, 
  deleteObject 
} from 'firebase/storage';
import { db, storage } from '../firebase';
import { useAuth } from './AuthContext';
import { 
  FaBold, 
  FaItalic, 
  FaUnderline, 
  FaStrikethrough,
  FaEdit,
  FaTrash,
  FaTimes,
  FaAlignLeft,
  FaAlignCenter,
  FaAlignRight,
  FaUndo,
  FaRedo,
  FaLink,
  FaEye,
  FaEyeSlash,
  FaImage,
  FaCalendarAlt,
  FaUser,
  FaTag,
  FaStar,
  FaHeart,
  FaShare,
  FaBookmark
} from 'react-icons/fa';

const BlogContext = createContext();

export function useBlog() {
  return useContext(BlogContext);
}

// ── Rich Text Editor ──────────────────────────────────────────────────────────
const EDITOR_CSS = `
  .rte-content { min-height:220px; max-height:500px; overflow-y:auto; padding:14px 16px; outline:none; font-size:15px; line-height:1.7; word-break:break-word; }
  .rte-content:empty:before { content:attr(data-ph); color:#9ca3af; pointer-events:none; }
  .rte-content h1 { font-size:1.75em; font-weight:700; margin:.5em 0; }
  .rte-content h2 { font-size:1.4em; font-weight:700; margin:.5em 0; }
  .rte-content h3 { font-size:1.15em; font-weight:600; margin:.5em 0; }
  .rte-content a { color:#2563eb; text-decoration:underline; cursor:pointer; word-break:break-all; }
  .rte-content a:hover { color:#1d4ed8; }
  .rte-content img { max-width:100%; border-radius:8px; margin:8px 0; display:block; }
  .rte-content ul { list-style:disc; padding-left:1.5em; margin:.4em 0; }
  .rte-content ol { list-style:decimal; padding-left:1.5em; margin:.4em 0; }
  .rte-content blockquote { border-left:3px solid #e5e7eb; margin:.5em 0; padding:.4em 1em; color:#6b7280; font-style:italic; }
  .rte-content table { width:100%; border-collapse:collapse; margin:.5em 0; }
  .rte-content td, .rte-content th { border:1px solid #e5e7eb; padding:6px 10px; }
  .rte-content th { background:#f9fafb; font-weight:600; }
  .blog-post-content { font-size:15px; line-height:1.8; color:#374151; word-break:break-word; }
  .blog-post-content h1 { font-size:1.75em; font-weight:700; margin:1em 0 .4em; color:#111827; }
  .blog-post-content h2 { font-size:1.4em; font-weight:700; margin:.9em 0 .35em; color:#111827; }
  .blog-post-content h3 { font-size:1.15em; font-weight:600; margin:.8em 0 .3em; color:#111827; }
  .blog-post-content p { margin:0 0 .9em; }
  .blog-post-content a { color:#2563eb; text-decoration:underline; cursor:pointer; word-break:break-all; overflow-wrap:anywhere; }
  .blog-post-content a:hover { color:#1d4ed8; }
  .blog-post-content img { max-width:100%; border-radius:10px; margin:1em 0; display:block; box-shadow:0 2px 8px rgba(0,0,0,.08); }
  .blog-post-content ul { list-style:disc; padding-left:1.5em; margin:.6em 0; }
  .blog-post-content ol { list-style:decimal; padding-left:1.5em; margin:.6em 0; }
  .blog-post-content li { margin:.25em 0; }
  .blog-post-content blockquote { border-left:4px solid #f97316; margin:.8em 0; padding:.5em 1em; background:#fff7ed; border-radius:0 8px 8px 0; color:#92400e; }
  .blog-post-content table { width:100%; border-collapse:collapse; margin:1em 0; font-size:.95em; }
  .blog-post-content td,.blog-post-content th { border:1px solid #e5e7eb; padding:8px 12px; }
  .blog-post-content th { background:#f9fafb; font-weight:600; }
  .blog-post-content strong,.blog-post-content b { font-weight:700; color:#111827; }
`;
if (typeof document !== 'undefined' && !document.getElementById('blog-rte-style')) {
  const s = document.createElement('style');
  s.id = 'blog-rte-style';
  s.textContent = EDITOR_CSS;
  document.head.appendChild(s);
}

const RichTextEditor = ({ value, onChange, placeholder = 'Write your content here...' }) => {
  const editorRef = useRef(null);
  const fileInputRef = useRef(null);
  const savedRange = useRef(null);
  const [showLinkModal, setShowLinkModal] = useState(false);
  const [linkUrl, setLinkUrl] = useState('');
  const [linkText, setLinkText] = useState('');
  const [isUploading, setIsUploading] = useState(false);
  const [activeFormats, setActiveFormats] = useState({});

  // Sync value into editor only when it differs
  useEffect(() => {
    if (editorRef.current && editorRef.current.innerHTML !== (value || '')) {
      editorRef.current.innerHTML = value || '';
    }
  }, [value]);

  const saveRange = useCallback(() => {
    const sel = window.getSelection();
    if (sel && sel.rangeCount > 0) {
      const r = sel.getRangeAt(0);
      savedRange.current = {
        sc: r.startContainer, so: r.startOffset,
        ec: r.endContainer,   eo: r.endOffset,
      };
      // Update active formats
      try {
        setActiveFormats({
          bold: document.queryCommandState('bold'),
          italic: document.queryCommandState('italic'),
          underline: document.queryCommandState('underline'),
          strikeThrough: document.queryCommandState('strikeThrough'),
        });
      } catch (_) {}
    }
  }, []);

  const restoreRange = useCallback(() => {
    if (!savedRange.current) return null;
    try {
      const r = document.createRange();
      r.setStart(savedRange.current.sc, savedRange.current.so);
      r.setEnd(savedRange.current.ec, savedRange.current.eo);
      const sel = window.getSelection();
      sel.removeAllRanges();
      sel.addRange(r);
      return r;
    } catch (_) { return null; }
  }, []);

  const exec = useCallback((cmd, val = null) => {
    editorRef.current?.focus();
    try { document.execCommand(cmd, false, val); } catch (_) {}
    if (editorRef.current) onChange(editorRef.current.innerHTML);
  }, [onChange]);

  const handleKeyDown = useCallback((e) => {
    if (e.ctrlKey || e.metaKey) {
      switch (e.key) {
        case 'b': e.preventDefault(); exec('bold'); break;
        case 'i': e.preventDefault(); exec('italic'); break;
        case 'u': e.preventDefault(); exec('underline'); break;
        case 'k': e.preventDefault(); handleLinkClick(); break;
        case 'z': e.preventDefault(); exec(e.shiftKey ? 'redo' : 'undo'); break;
        default: break;
      }
    }
  }, [exec]);

  const handleLinkClick = () => {
    saveRange();
    const sel = window.getSelection();
    setLinkText(sel?.toString() || '');
    setLinkUrl('');
    setShowLinkModal(true);
  };

  const insertLink = () => {
    if (!linkUrl) return;
    const url = linkUrl.startsWith('http') ? linkUrl : `https://${linkUrl}`;
    editorRef.current?.focus();
    const range = restoreRange();
    const a = document.createElement('a');
    a.href = url;
    a.textContent = linkText || url;
    a.target = '_blank';
    a.rel = 'noopener noreferrer';
    a.style.color = '#2563eb';
    a.style.textDecoration = 'underline';
    try {
      if (range) {
        range.deleteContents();
        range.insertNode(a);
        const after = document.createRange();
        after.setStartAfter(a);
        after.collapse(true);
        window.getSelection().removeAllRanges();
        window.getSelection().addRange(after);
      } else {
        editorRef.current.appendChild(a);
      }
    } catch (_) {
      editorRef.current.innerHTML += a.outerHTML;
    }
    onChange(editorRef.current.innerHTML);
    setShowLinkModal(false);
    setLinkUrl(''); setLinkText('');
  };

  const handleImageUpload = async (file) => {
    if (!file?.type.startsWith('image/')) return;
    if (file.size > 5 * 1024 * 1024) { alert('Max 5MB'); return; }
    setIsUploading(true);
    try {
      const { ref: sRef, uploadBytes, getDownloadURL } = await import('firebase/storage');
      const { storage: st } = await import('../firebase');
      const r = sRef(st, `blog-images/${Date.now()}-${file.name}`);
      const snap = await uploadBytes(r, file);
      const url = await getDownloadURL(snap.ref);
      editorRef.current?.focus();
      document.execCommand('insertHTML', false, `<img src="${url}" alt="image" loading="lazy" style="max-width:100%;border-radius:8px;margin:8px 0;" />`);
      onChange(editorRef.current.innerHTML);
    } catch (e) { console.error(e); alert('Upload failed'); }
    finally { setIsUploading(false); if (fileInputRef.current) fileInputRef.current.value = ''; }
  };

  const Btn = ({ cmd, val, title, children, active }) => (
    <button type="button" onMouseDown={e => { e.preventDefault(); exec(cmd, val); }}
      title={title}
      className={`p-1.5 rounded text-sm transition-colors ${active ? 'bg-orange-100 text-orange-700' : 'hover:bg-gray-200 text-gray-700'}`}>
      {children}
    </button>
  );

  return (
    <div className="border border-gray-200 rounded-xl overflow-hidden bg-white shadow-sm">
      {/* Toolbar */}
      <div className="bg-gray-50 border-b border-gray-200 px-2 py-1.5 flex flex-wrap gap-0.5 items-center">
        <Btn cmd="bold" title="Bold (Ctrl+B)" active={activeFormats.bold}><FaBold className="w-3.5 h-3.5"/></Btn>
        <Btn cmd="italic" title="Italic (Ctrl+I)" active={activeFormats.italic}><FaItalic className="w-3.5 h-3.5"/></Btn>
        <Btn cmd="underline" title="Underline (Ctrl+U)" active={activeFormats.underline}><FaUnderline className="w-3.5 h-3.5"/></Btn>
        <Btn cmd="strikeThrough" title="Strikethrough" active={activeFormats.strikeThrough}><FaStrikethrough className="w-3.5 h-3.5"/></Btn>

        <div className="w-px h-5 bg-gray-300 mx-1"/>

        <select
          onFocus={saveRange}
          onChange={e => { if (e.target.value) { exec('formatBlock', e.target.value); } e.target.value = ''; }}
          className="text-xs border border-gray-200 rounded px-1.5 py-1 bg-white text-gray-700 h-7">
          <option value="">Paragraph</option>
          <option value="h1">Heading 1</option>
          <option value="h2">Heading 2</option>
          <option value="h3">Heading 3</option>
          <option value="blockquote">Quote</option>
        </select>

        <div className="w-px h-5 bg-gray-300 mx-1"/>

        <Btn cmd="justifyLeft" title="Left"><FaAlignLeft className="w-3.5 h-3.5"/></Btn>
        <Btn cmd="justifyCenter" title="Center"><FaAlignCenter className="w-3.5 h-3.5"/></Btn>
        <Btn cmd="justifyRight" title="Right"><FaAlignRight className="w-3.5 h-3.5"/></Btn>

        <div className="w-px h-5 bg-gray-300 mx-1"/>

        <Btn cmd="insertUnorderedList" title="Bullet list">
          <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 10h16M4 14h16M4 18h16"/></svg>
        </Btn>
        <Btn cmd="insertOrderedList" title="Numbered list">
          <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 20 20"><path d="M3 4a1 1 0 000 2h.01a1 1 0 000-2H3zm3 0a1 1 0 000 2h10a1 1 0 000-2H6zM3 9a1 1 0 000 2h.01a1 1 0 000-2H3zm3 0a1 1 0 000 2h10a1 1 0 000-2H6zm-3 5a1 1 0 000 2h.01a1 1 0 000-2H3zm3 0a1 1 0 000 2h10a1 1 0 000-2H6z"/></svg>
        </Btn>

        <div className="w-px h-5 bg-gray-300 mx-1"/>

        <select
          onFocus={saveRange}
          onChange={e => { if (e.target.value) { exec('foreColor', e.target.value); } e.target.value = ''; }}
          className="text-xs border border-gray-200 rounded px-1.5 py-1 bg-white text-gray-700 h-7">
          <option value="">Color</option>
          {[['Black','#111827'],['Red','#dc2626'],['Blue','#2563eb'],['Green','#16a34a'],['Orange','#f97316'],['Purple','#9333ea']].map(([n,v]) => <option key={v} value={v}>{n}</option>)}
        </select>

        <select
          onFocus={saveRange}
          onChange={e => { if (e.target.value) { exec('hiliteColor', e.target.value); } e.target.value = ''; }}
          className="text-xs border border-gray-200 rounded px-1.5 py-1 bg-white text-gray-700 h-7">
          <option value="">Highlight</option>
          {[['Yellow','#fef08a'],['Green','#bbf7d0'],['Blue','#bfdbfe'],['Pink','#fbcfe8'],['Orange','#fed7aa'],['Gray','#e5e7eb']].map(([n,v]) => <option key={v} value={v}>{n}</option>)}
        </select>

        <div className="w-px h-5 bg-gray-300 mx-1"/>

        <button type="button" onClick={e => { e.preventDefault(); saveRange(); fileInputRef.current?.click(); }}
          disabled={isUploading} title="Upload image"
          className="p-1.5 rounded hover:bg-gray-200 text-gray-700 disabled:opacity-40 transition-colors">
          {isUploading
            ? <div className="w-3.5 h-3.5 border-2 border-orange-500 border-t-transparent rounded-full animate-spin"/>
            : <FaImage className="w-3.5 h-3.5"/>}
        </button>

        <button type="button" onClick={e => { e.preventDefault(); handleLinkClick(); }}
          title="Insert link (Ctrl+K)"
          className="p-1.5 rounded hover:bg-gray-200 text-gray-700 transition-colors">
          <FaLink className="w-3.5 h-3.5"/>
        </button>

        <div className="w-px h-5 bg-gray-300 mx-1"/>

        <Btn cmd="undo" title="Undo (Ctrl+Z)"><FaUndo className="w-3.5 h-3.5"/></Btn>
        <Btn cmd="redo" title="Redo (Ctrl+Shift+Z)"><FaRedo className="w-3.5 h-3.5"/></Btn>
      </div>

      {/* Editable area */}
      <div
        ref={editorRef}
        contentEditable
        suppressContentEditableWarning
        className="rte-content focus:outline-none"
        data-ph={placeholder}
        onInput={() => { if (editorRef.current) onChange(editorRef.current.innerHTML); }}
        onKeyDown={handleKeyDown}
        onMouseUp={saveRange}
        onKeyUp={saveRange}
        onPaste={e => {
          e.preventDefault();
          const text = e.clipboardData.getData('text/plain');
          document.execCommand('insertText', false, text);
          onChange(editorRef.current.innerHTML);
        }}
        onClick={e => {
          const a = e.target.closest('a');
          if (a?.href && (e.ctrlKey || e.metaKey)) {
            e.preventDefault();
            window.open(a.href, '_blank', 'noopener,noreferrer');
          }
          saveRange();
        }}
      />

      <input ref={fileInputRef} type="file" accept="image/*" className="hidden"
        onChange={e => { const f = e.target.files?.[0]; if (f) handleImageUpload(f); }}/>

      {/* Link modal */}
      {showLinkModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[9999] p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm">
            <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between">
              <span className="font-semibold text-gray-900">Insert Link</span>
              <button type="button" onClick={() => setShowLinkModal(false)} className="w-7 h-7 bg-gray-100 hover:bg-gray-200 rounded-full flex items-center justify-center">
                <FaTimes className="w-3 h-3 text-gray-500"/>
              </button>
            </div>
            <div className="p-5 space-y-3">
              <div>
                <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">URL *</label>
                <input type="url" value={linkUrl} onChange={e => setLinkUrl(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && insertLink()}
                  className="w-full px-3 py-2.5 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-orange-400 focus:border-transparent outline-none"
                  placeholder="https://example.com" autoFocus/>
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">Display text</label>
                <input type="text" value={linkText} onChange={e => setLinkText(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && insertLink()}
                  className="w-full px-3 py-2.5 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-orange-400 focus:border-transparent outline-none"
                  placeholder="Link text (optional)"/>
              </div>
              <div className="flex gap-2 pt-1">
                <button type="button" onClick={() => setShowLinkModal(false)}
                  className="flex-1 py-2.5 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-xl font-semibold text-sm transition-all">Cancel</button>
                <button type="button" onClick={insertLink} disabled={!linkUrl}
                  className="flex-1 py-2.5 bg-orange-500 hover:bg-orange-600 text-white rounded-xl font-semibold text-sm transition-all disabled:opacity-50">Insert</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};


// Other components remain the same...
const ConfirmationModal = ({ isOpen, onClose, onConfirm, title, message, confirmText = "Confirm", cancelText = "Cancel", type = "danger" }) => {
  if (!isOpen) return null;

  const buttonColors = {
    danger: "bg-red-600 hover:bg-red-700",
    success: "bg-green-600 hover:bg-green-700",
    warning: "bg-yellow-600 hover:bg-yellow-700",
    info: "bg-blue-600 hover:bg-blue-700"
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999] p-4">
      <div className="bg-white rounded-lg w-full max-w-md">
        <div className="p-4 sm:p-6 max-w-4xl mx-auto">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">{title}</h3>
          <p className="text-gray-600 mb-6">{message}</p>
          
          <div className="flex justify-center space-x-4">
            <button
              onClick={onClose}
              className="px-4 py-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
            >
              {cancelText}
            </button>
            <button
              onClick={onConfirm}
              className={`px-4 py-2 text-white rounded-lg transition-colors ${buttonColors[type]}`}
            >
              {confirmText}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const ToastModal = ({ isOpen, onClose, message, type = "success" }) => {
  useEffect(() => {
    if (isOpen) {
      const timer = setTimeout(() => {
        onClose();
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const bgColors = {
    success: "bg-green-100 text-green-700",
    error: "bg-red-100 text-red-700",
    warning: "bg-yellow-100 text-yellow-700",
    info: "bg-blue-100 text-blue-700"
  };

  const icons = {
    success: "✓",
    error: "✗",
    warning: "⚠",
    info: "ℹ"
  };

  return (
    <div className="fixed top-4 left-1/2 transform -translate-x-1/2 z-[9999]">
      <div className={`${bgColors[type]} px-3 py-2 rounded-lg shadow-lg flex items-center min-w-64 max-w-md mx-4`}>
        <span className="text-lg mr-3">{icons[type]}</span>
        <span className="flex-1 text-m">{message}</span>
      </div>
    </div>
  );
};

// Mobile-Friendly Blog Editor Modal (enhanced)
// ── Blog Editor Modal ─────────────────────────────────────────────────────────
const BlogEditorModal = ({ isOpen, onClose, blog = null }) => {
  const { currentUser } = useAuth();
  const { createBlog, updateBlog } = useBlog();
  const [formData, setFormData] = useState({ title:'', content:'', summary:'', featured:false, hidden:false, tags:'' });
  const [loading, setLoading] = useState(false);
  const [toast, setToast] = useState({ show:false, message:'', type:'success' });
  const [confirmClose, setConfirmClose] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    setFormData(blog ? {
      title: blog.title || '', content: blog.content || '', summary: blog.summary || '',
      featured: blog.featured || false, hidden: blog.hidden || false,
      tags: blog.tags ? blog.tags.join(', ') : ''
    } : { title:'', content:'', summary:'', featured:false, hidden:false, tags:'' });
  }, [blog, isOpen]);

  const isDirty = () => {
    if (!blog) return !!(formData.title || formData.content || formData.summary || formData.tags);
    return formData.title !== (blog.title||'') || formData.content !== (blog.content||'') ||
      formData.summary !== (blog.summary||'') || formData.featured !== (blog.featured||false) ||
      formData.hidden !== (blog.hidden||false) || formData.tags !== (blog.tags?.join(', ')||'');
  };

  const handleClose = () => { if (isDirty()) setConfirmClose(true); else onClose(); };

  const handleSubmit = async e => {
    e.preventDefault();
    setLoading(true);
    try {
      const data = {
        title: formData.title.trim(), content: formData.content, summary: formData.summary.trim(),
        featured: formData.featured, hidden: formData.hidden,
        tags: formData.tags.split(',').map(t => t.trim()).filter(Boolean),
        authorId: currentUser.uid, authorName: currentUser.displayName || 'Admin',
      };
      if (blog) { await updateBlog(blog.id, data); setToast({ show:true, message:'Post updated!', type:'success' }); }
      else { await createBlog(data); setToast({ show:true, message:'Post created!', type:'success' }); }
      setTimeout(onClose, 1200);
    } catch (err) {
      console.error(err);
      setToast({ show:true, message:'Error saving post', type:'error' });
    } finally { setLoading(false); }
  };

  if (!isOpen) return null;

  return (
    <>
      <div className="fixed inset-0 bg-white z-[9999] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100 bg-white flex-shrink-0">
          <div className="flex items-center gap-3">
            <button type="button" onClick={handleClose}
              className="w-8 h-8 flex items-center justify-center rounded-full bg-gray-100 hover:bg-gray-200 transition-colors">
              <FaTimes className="w-3.5 h-3.5 text-gray-600"/>
            </button>
            <span className="font-bold text-gray-900">{blog ? 'Edit Post' : 'New Post'}</span>
          </div>
          <div className="flex items-center gap-2">
            {isDirty() && <span className="text-xs text-amber-600 bg-amber-50 px-2 py-1 rounded-full font-medium">Unsaved</span>}
            <button type="button" onClick={handleSubmit}
              disabled={loading || !formData.title.trim() || !formData.content}
              className="px-5 py-2 bg-orange-500 hover:bg-orange-600 text-white rounded-xl font-semibold text-sm disabled:opacity-50 transition-all">
              {loading ? 'Saving...' : blog ? 'Update' : 'Publish'}
            </button>
          </div>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto">
          <div className="max-w-3xl mx-auto px-4 py-4 space-y-4 pb-8">
            {/* Title */}
            <input type="text" value={formData.title}
              onChange={e => setFormData({...formData, title: e.target.value})}
              className="w-full text-xl font-bold border-0 border-b border-gray-200 pb-2 focus:outline-none focus:border-orange-400 placeholder-gray-300 transition-colors"
              placeholder="Post title..." required/>

            {/* Summary */}
            <textarea value={formData.summary} rows={2}
              onChange={e => setFormData({...formData, summary: e.target.value})}
              className="w-full text-sm text-gray-600 border border-gray-200 rounded-xl px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-orange-400 focus:border-transparent resize-none placeholder-gray-400"
              placeholder="Short summary (optional) — shown in the card preview"/>

            {/* Rich text editor */}
            <div>
              <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">Content *</div>
              <RichTextEditor
                value={formData.content}
                onChange={c => setFormData({...formData, content: c})}
                placeholder="Write your post content here..."/>
            </div>

            {/* Tags + options */}
            <div className="border border-gray-100 rounded-xl p-4 space-y-3 bg-gray-50">
              <div>
                <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">Tags</label>
                <input type="text" value={formData.tags}
                  onChange={e => setFormData({...formData, tags: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-orange-400 focus:border-transparent bg-white"
                  placeholder="badminton, events, news (comma separated)"/>
              </div>
              <div className="flex gap-6">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" checked={formData.featured}
                    onChange={e => setFormData({...formData, featured: e.target.checked})}
                    className="w-4 h-4 accent-orange-500 rounded"/>
                  <span className="text-sm text-gray-700 font-medium">Featured</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" checked={formData.hidden}
                    onChange={e => setFormData({...formData, hidden: e.target.checked})}
                    className="w-4 h-4 accent-red-500 rounded"/>
                  <span className="text-sm text-gray-700 font-medium">Hidden (draft)</span>
                </label>
              </div>
            </div>
          </div>
        </form>
      </div>

      {confirmClose && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[10000] p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm p-6 text-center">
            <div className="w-12 h-12 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-3">
              <svg className="w-5 h-5 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/></svg>
            </div>
            <h3 className="font-bold text-gray-900 mb-1">Discard changes?</h3>
            <p className="text-sm text-gray-500 mb-5">Your unsaved changes will be lost.</p>
            <div className="flex gap-2">
              <button onClick={() => setConfirmClose(false)} className="flex-1 py-2.5 bg-gray-100 text-gray-700 rounded-xl font-semibold text-sm hover:bg-gray-200">Keep editing</button>
              <button onClick={() => { setConfirmClose(false); onClose(); }} className="flex-1 py-2.5 bg-red-500 text-white rounded-xl font-semibold text-sm hover:bg-red-600">Discard</button>
            </div>
          </div>
        </div>
      )}

      <ToastModal isOpen={toast.show} onClose={() => setToast({...toast, show:false})} message={toast.message} type={toast.type}/>
    </>
  );
};


// ── Blog Detail Modal ─────────────────────────────────────────────────────────
const BlogDetailModal = ({ isOpen, onClose, blog }) => {
  const { isAdmin } = useAuth();
  const { deleteBlog } = useBlog();
  const [showDelete, setShowDelete] = useState(false);
  const [toast, setToast] = useState({ show:false, message:'', type:'success' });
  const [copied, setCopied] = useState(false);

  if (!isOpen || !blog) return null;

  const formatDate = d => {
    if (!d) return '';
    const dt = d.toDate ? d.toDate() : new Date(d);
    return dt.toLocaleDateString('en-AU', { day:'numeric', month:'long', year:'numeric' });
  };

  const wordCount = () => {
    const div = document.createElement('div');
    div.innerHTML = blog.content;
    const words = (div.textContent||'').split(/\s+/).filter(Boolean).length;
    const mins = Math.max(1, Math.ceil(words / 250));
    return `${mins} min read`;
  };

  const handleDelete = async () => {
    try {
      await deleteBlog(blog.id);
      setToast({ show:true, message:'Post deleted', type:'success' });
      setTimeout(onClose, 1200);
    } catch { setToast({ show:true, message:'Error deleting post', type:'error' }); }
  };

  const handleShare = async () => {
    const url = `${window.location.origin}/blog/${blog.id}`;
    try { await navigator.clipboard.writeText(url); setCopied(true); setTimeout(() => setCopied(false), 2000); } catch {}
  };

  // Prep content: add target _blank to all links
  const safeContent = (blog.content || '').replace(/<a /gi, '<a target="_blank" rel="noopener noreferrer" ');

  return (
    <>
      <div className="fixed inset-0 bg-white z-[9999] flex flex-col">
        {/* Top bar */}
        <div className="flex items-center gap-3 px-4 py-3 border-b border-gray-100 flex-shrink-0 bg-white">
          <button onClick={onClose}
            className="w-8 h-8 flex items-center justify-center rounded-full bg-gray-100 hover:bg-gray-200 transition-colors flex-shrink-0">
            <svg className="w-4 h-4 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7"/></svg>
          </button>
          <span className="flex-1 text-sm font-semibold text-gray-600 truncate">Club News</span>
          {(blog.viewCount||0) > 0 && (
            <span className="flex items-center gap-1 text-xs text-gray-400">
              <FaEye className="w-3 h-3"/>{blog.viewCount}
            </span>
          )}
          <button onClick={handleShare}
            className="flex items-center gap-1.5 px-3 py-1.5 bg-gray-100 hover:bg-gray-200 rounded-lg text-xs font-medium text-gray-600 transition-colors">
            {copied
              ? <><svg className="w-3.5 h-3.5 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7"/></svg>Copied!</>
              : <><FaShare className="w-3 h-3"/>Share</>}
          </button>
          {isAdmin && (
            <button onClick={() => setShowDelete(true)}
              className="w-8 h-8 flex items-center justify-center rounded-full text-red-400 hover:bg-red-50 transition-colors">
              <FaTrash className="w-3.5 h-3.5"/>
            </button>
          )}
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto">
          <div className="max-w-2xl mx-auto px-4 py-5 pb-16">
            {/* Badges */}
            <div className="flex items-center gap-2 flex-wrap mb-3">
              {blog.featured && <span className="px-2 py-0.5 bg-amber-100 text-amber-700 text-xs font-semibold rounded-full flex items-center gap-1"><FaStar className="w-2.5 h-2.5"/>Featured</span>}
              {blog.hidden && <span className="px-2 py-0.5 bg-red-100 text-red-600 text-xs font-semibold rounded-full flex items-center gap-1"><FaEyeSlash className="w-2.5 h-2.5"/>Hidden</span>}
              {blog.tags?.map((tag, i) => <span key={i} className="px-2 py-0.5 bg-gray-100 text-gray-600 text-xs rounded-full">{tag}</span>)}
            </div>

            {/* Title */}
            <h1 className="text-2xl font-bold text-gray-900 leading-tight mb-3">{blog.title}</h1>

            {/* Meta */}
            <div className="flex items-center gap-4 text-xs text-gray-400 mb-1 flex-wrap">
              {blog.authorName && <span className="flex items-center gap-1"><FaUser className="w-3 h-3"/>{blog.authorName}</span>}
              {blog.createdAt && <span className="flex items-center gap-1"><FaCalendarAlt className="w-3 h-3"/>{formatDate(blog.createdAt)}</span>}
              <span>{wordCount()}</span>
            </div>

            {/* Summary */}
            {blog.summary && (
              <div className="bg-orange-50 border-l-3 border-orange-400 px-4 py-3 rounded-r-xl my-4 text-sm text-gray-700 leading-relaxed" style={{borderLeftWidth:'3px',borderLeftColor:'#f97316'}}>
                {blog.summary}
              </div>
            )}

            {/* Body */}
            <div
              className="blog-post-content mt-4"
              dangerouslySetInnerHTML={{ __html: safeContent }}
              onClick={e => {
                const a = e.target.closest('a');
                if (a?.href) { e.preventDefault(); e.stopPropagation(); window.open(a.href, '_blank', 'noopener,noreferrer'); }
              }}
            />
          </div>
        </div>
      </div>

      {showDelete && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[10000] p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm p-6 text-center">
            <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-3">
              <FaTrash className="w-5 h-5 text-red-500"/>
            </div>
            <h3 className="font-bold text-gray-900 mb-1">Delete post?</h3>
            <p className="text-sm text-gray-500 mb-1">"{blog.title}"</p>
            <p className="text-xs text-red-400 mb-5">This cannot be undone.</p>
            <div className="flex gap-2">
              <button onClick={() => setShowDelete(false)} className="flex-1 py-2.5 bg-gray-100 text-gray-700 rounded-xl font-semibold text-sm hover:bg-gray-200">Cancel</button>
              <button onClick={handleDelete} className="flex-1 py-2.5 bg-red-500 text-white rounded-xl font-semibold text-sm hover:bg-red-600">Delete</button>
            </div>
          </div>
        </div>
      )}

      <ToastModal isOpen={toast.show} onClose={() => setToast({...toast, show:false})} message={toast.message} type={toast.type}/>
    </>
  );
};


export function BlogProvider({ children }) {
  const { currentUser } = useAuth();
  const [blogs, setBlogs] = useState([]);
  const [loading, setLoading] = useState(true);

  // Real-time blog subscription
  useEffect(() => {
    const blogQuery = query(
      collection(db, 'blogs'),
      orderBy('createdAt', 'desc')
    );
    
    // Set up real-time listener
    const unsubscribe = onSnapshot(blogQuery, (snapshot) => {
      const blogList = [];
      snapshot.forEach(doc => {
        blogList.push({ id: doc.id, ...doc.data() });
      });
      
      setBlogs(blogList);
      setLoading(false);
    }, (error) => {
      console.error('Error fetching blogs:', error);
      setLoading(false);
    });

    // Cleanup subscription on unmount
    return unsubscribe;
  }, []);

  // Create new blog
  const createBlog = async (blogData) => {
    try {
      const docRef = await addDoc(collection(db, 'blogs'), {
        ...blogData,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      });
      
      return docRef.id;
    } catch (error) {
      console.error('Error creating blog:', error);
      throw error;
    }
  };

  // Update existing blog
  const updateBlog = async (id, blogData) => {
    try {
      const blogRef = doc(db, 'blogs', id);
      await updateDoc(blogRef, {
        ...blogData,
        updatedAt: serverTimestamp()
      });
    } catch (error) {
      console.error('Error updating blog:', error);
      throw error;
    }
  };

  // Delete blog (with image cleanup)
  const deleteBlog = async (id) => {
    try {
      // Get blog data to clean up images
      const blogToDelete = blogs.find(blog => blog.id === id);
      
      if (blogToDelete && blogToDelete.content) {
        // Extract image URLs from content and delete from storage
        const div = document.createElement('div');
        div.innerHTML = blogToDelete.content;
        const images = div.querySelectorAll('img');
        
        for (const img of images) {
          const src = img.src;
          if (src && src.includes('firebase')) {
            try {
              // Extract path from Firebase URL
              const url = new URL(src);
              const pathMatch = url.pathname.match(/\/o\/(.+?)\?/);
              if (pathMatch) {
                const imagePath = decodeURIComponent(pathMatch[1]);
                const imageRef = ref(storage, imagePath);
                await deleteObject(imageRef);
              }
            } catch (imageError) {
              console.warn('Error deleting image:', imageError);
            }
          }
        }
      }
      
      // Delete the blog document
      await deleteDoc(doc(db, 'blogs', id));
    } catch (error) {
      console.error('Error deleting blog:', error);
      throw error;
    }
  };

  const value = {
    blogs,
    loading,
    createBlog,
    updateBlog,
    deleteBlog,
    BlogEditorModal,
    BlogDetailModal,
  };

  return (
    <BlogContext.Provider value={value}>
      {children}
    </BlogContext.Provider>
  );
}